import { useState, useEffect, useMemo } from "react";
import { Select } from "antd";
import { OrganizationLogoMark } from "./OrganizationLogoMark";
import { useMaxWidth } from "../../hooks/useMaxWidth";

const { Option } = Select;

export const SelectOrganizations = ({
    organizations,
    selectedOrganizationId,
    handleSelectChange,
    noIcon,
    placeholder,
    /** When true, fill the navbar pill only - avoids 25vw stretching the bar */
    embedInNav,
}: {
    organizations: any[];
    selectedOrganizationId: any;
    handleSelectChange: (value: string) => void;
    noIcon?: boolean;
    placeholder?: string;
    embedInNav?: boolean;
}) => {
    const isMobile = useMaxWidth(767);

    // Initialize state directly with the selectedOrganizationId
    const [selectedId, setSelectedId] = useState<string | undefined>(selectedOrganizationId);

    const selectedOrganization = useMemo(
        () => organizations?.find((org) => org._id === selectedId),
        [organizations, selectedId]
    );

    // Ensure the local state is updated when the selectedOrganizationId prop changes
    useEffect(() => {
        setSelectedId(selectedOrganizationId);
    }, [selectedOrganizationId]);

    // Update local state and propagate changes upwards via handleSelectChange
    const handleChange = (value: string) => {
        setSelectedId(value);
        handleSelectChange(value);
    };

    const selectStyle = embedInNav
        ? { minWidth: 0 }
        : { width: isMobile ? "65vw" : "25vw" };

    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                minWidth: 0,
            }}
        >
            <Select
                showSearch
                optionLabelProp="label"
                variant={embedInNav ? "borderless" : "outlined"}
                className={embedInNav ? "nav-context__select" : undefined}
                style={selectStyle}
                popupMatchSelectWidth={embedInNav ? false : true}
                classNames={
                    embedInNav
                        ? { popup: { root: "nav-context__select-dropdown" } }
                        : undefined
                }
                styles={
                    embedInNav
                        ? { popup: { root: { maxWidth: "90vw" } } }
                        : undefined
                }
                labelRender={({ label }) => {
                    const name =
                        typeof label === "string"
                            ? label
                            : selectedOrganization?.name;
                    return (
                        <span
                            className="nav-context__select-label"
                            title={name}
                        >
                            {!embedInNav && (
                                <OrganizationLogoMark
                                    name={selectedOrganization?.name}
                                    logo={
                                        selectedOrganization?.logoBase64 ||
                                        selectedOrganization?.logo
                                    }
                                    size={16}
                                />
                            )}
                            <span className="nav-context__select-text">
                                {name ?? label}
                            </span>
                        </span>
                    );
                }}
                filterOption={(input: any, option: any) => {
                    const searchValue = input?.toLowerCase();
                    // Use the data-name attribute (plain org name) for search,
                    // since option.children is now JSX (name + badge spans).
                    const name = option?.['data-name'] || '';
                    return name.toLowerCase().includes(searchValue);
                }}
                value={selectedId}
                onChange={handleChange}
                placeholder={placeholder || "Select organization"}
            >
                {organizations?.map((organization) => (
                    <Option
                        key={organization._id}
                        value={organization._id}
                        label={organization.name}
                        data-name={organization.name}
                    >
                        <span className="nav-context__option">
                            <OrganizationLogoMark
                                name={organization.name}
                                logo={organization.logoBase64 || organization.logo}
                                size={18}
                            />
                            <span className="nav-context__option-text">
                                {organization.name}
                                {organization.isMasterOrganization && (
                                    <span className="nav-context__option-badge nav-context__option-badge--master">
                                        Master
                                    </span>
                                )}
                                {organization.parentOrganizationId && (
                                    <span className="nav-context__option-badge nav-context__option-badge--sub">
                                        Sub-org
                                    </span>
                                )}
                            </span>
                        </span>
                    </Option>
                ))}
            </Select>
        </div>
    );
};
